import { Colors, Icon, Spinner } from '@amelio/ui-library';
import { Fragment, type ReactElement, useEffect, useRef, useState } from 'react';

import { useAmelioTranslations } from '@/hooks';

/**
 * An AI insight for one question, split by the data source it draws from. Each section is a single
 * string: the backend generates in the caller's app language, so there is only ever one version.
 */
export interface QuestionInsight {
    engagement: string;
    peer360: string;
    self: string;
}

interface InsightsPanelProps {
    /** The current question's AI insight (three sections). Undefined until generated. */
    insight?: QuestionInsight;
    /** Error message when this question's generation failed; shows an error state with a retry. */
    error?: string;
    /** True while this question's result is still pending after a generation run. */
    isLoading: boolean;
    /** Kicks off AI insight generation for every question (via the RAG gateway). */
    onGenerate?: () => void;
    /** Retries generation for just this (failed) question. */
    onRetry?: () => void;
    /** True while a generation run is in flight (button disabled). */
    isGenerating?: boolean;
    /** Disables the Generate button (e.g. the evaluation period isn't set yet). */
    generateDisabled?: boolean;
    /** Tooltip shown on the disabled Generate button explaining why. */
    generateDisabledHint?: string;
    /** When false the "Insights from engagement" section is not rendered, even if data exists. */
    includeEngagement?: boolean;
    dataTestId?: string;
}

/**
 * Renders one section's body. Consecutive lines starting with a bullet marker (•, -, *) become a
 * bulleted list; everything else renders as paragraphs — so the model can return either prose or a
 * mix of prose and bullets and it reads correctly.
 */
const renderSectionBody = (text: string) => {
    const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    const blocks: ReactElement[] = [];
    let bullets: string[] = [];

    const flushBullets = (key: string) => {
        if (bullets.length === 0) return;
        blocks.push(
            <ul
                key={key}
                className={'flex list-disc flex-col gap-1.5 pl-5'}
            >
                {bullets.map((item, index) => (
                    <li
                        key={index}
                        className={'text-sm leading-relaxed text-gray-700'}
                    >
                        {item}
                    </li>
                ))}
            </ul>,
        );
        bullets = [];
    };

    lines.forEach((line, index) => {
        const bulletMatch = line.match(/^[•\-*]\s+(.*)$/);
        if (bulletMatch) {
            bullets.push(bulletMatch[1]);
            return;
        }
        flushBullets(`ul-${index}`);
        blocks.push(
            <p
                key={`p-${index}`}
                className={'text-sm leading-relaxed text-gray-700'}
            >
                {line}
            </p>,
        );
    });
    flushBullets('ul-end');

    return blocks;
};

export const InsightsPanel = ({
    insight,
    error,
    isLoading,
    onGenerate,
    onRetry,
    isGenerating = false,
    generateDisabled = false,
    generateDisabledHint,
    includeEngagement = true,
    dataTestId = 'insights-panel',
}: InsightsPanelProps) => {
    const { t } = useAmelioTranslations();

    const [copied, setCopied] = useState(false);
    const copyResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const sections = insight
        ? [
              ...(includeEngagement
                  ? [
                        {
                            key: 'engagement',
                            label: t('managerAssessmentEvaluation.insights.engagementSection'),
                            text: insight.engagement,
                        },
                    ]
                  : []),
              {
                  key: 'peer360',
                  label: t('managerAssessmentEvaluation.insights.peer360Section'),
                  text: insight.peer360,
              },
              {
                  key: 'self',
                  label: t('managerAssessmentEvaluation.insights.selfSection'),
                  text: insight.self,
              },
          ]
        : [];

    const hasInsight = Boolean(insight && sections.some((section) => section.text?.trim()));
    const showSpinner = !error && !hasInsight && (isLoading || isGenerating);
    // Nothing generated yet: the panel shows only a prominent Generate button, no text box (bug #15622).
    const showGenerateCta = !insight && !error && !showSpinner && Boolean(onGenerate);

    useEffect(() => () => clearTimeout(copyResetRef.current), []);

    const handleCopy = async () => {
        if (!insight) return;

        const parts = [
            includeEngagement &&
                insight.engagement &&
                `${t('managerAssessmentEvaluation.insights.engagementSection')}\n${insight.engagement}`,
            insight.peer360 &&
                `${t('managerAssessmentEvaluation.insights.peer360Section')}\n${insight.peer360}`,
            insight.self &&
                `${t('managerAssessmentEvaluation.insights.selfSection')}\n${insight.self}`,
        ]
            .filter((part): part is string => Boolean(part && part.trim()))
            .map((part) => part.trim());

        try {
            await navigator.clipboard.writeText(parts.join('\n\n'));
            setCopied(true);
            clearTimeout(copyResetRef.current);
            copyResetRef.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard unavailable (e.g. insecure context) — leave the button in its default state.
        }
    };

    return (
        <div
            className={
                'survey-insights-panel flex w-full flex-col gap-4 rounded-2xl border border-gray-200 bg-white/60 p-6 backdrop-blur-md md:max-w-[380px]'
            }
            data-testid={dataTestId}
        >
            <div className={'flex items-center gap-5.5'}>
                <h3 className={'text-sm font-medium text-gray-900'}>
                    {t('managerAssessmentEvaluation.insights.title')}
                </h3>

                <div
                    className={
                        'flex items-center gap-1.5 rounded-full border border-indigo-700 bg-white px-4 py-1'
                    }
                >
                    <Icon
                        name={'lucide-sparkles'}
                        color={Colors.SECONDARY_700}
                        className={
                            '!h-5 !w-5 bg-gradient-to-r from-indigo-700 to-pink-500 bg-clip-text text-transparent'
                        }
                    />

                    <span
                        className={
                            'bg-gradient-to-r from-indigo-700 to-pink-500 bg-clip-text text-[11px] font-medium text-transparent italic'
                        }
                    >
                        {t('managerAssessmentEvaluation.insights.aiGenerated')}
                    </span>
                </div>
            </div>

            {showGenerateCta ? (
                <button
                    type={'button'}
                    onClick={onGenerate}
                    disabled={isGenerating || generateDisabled}
                    title={generateDisabled ? generateDisabledHint : undefined}
                    className={
                        'flex w-full items-center justify-center gap-2.5 rounded-xl bg-purple-600 px-5 py-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60'
                    }
                    data-testid={'generate-insights-button'}
                >
                    <Icon
                        name={'lucide-sparkles'}
                        size={18}
                        color={Colors.WHITE}
                    />

                    {t('managerAssessmentEvaluation.insights.generate')}
                </button>
            ) : (
                <div
                    className={
                        'min-h-[150px] rounded-xl border border-gray-100 bg-white p-4 md:p-5'
                    }
                >
                    {showSpinner ? (
                        <div
                            className={'flex items-center justify-center py-8'}
                            data-testid={'insights-loading'}
                        >
                            <Spinner
                                size={'md'}
                                color={Colors.SECONDARY}
                            />
                        </div>
                    ) : error ? (
                        <div
                            className={'flex flex-col items-start gap-3 py-4'}
                            data-testid={'insights-error'}
                        >
                            <p className={'text-sm leading-relaxed text-red-600'}>
                                {t('managerAssessmentEvaluation.insights.generationFailed')}
                            </p>
                            {onRetry && (
                                <button
                                    type={'button'}
                                    onClick={onRetry}
                                    disabled={isGenerating}
                                    className={
                                        'flex items-center gap-2 rounded-lg border border-purple-600 px-4 py-2 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60'
                                    }
                                    data-testid={'retry-insights-button'}
                                >
                                    <Icon
                                        name={'arrow-rotate-right'}
                                        size={14}
                                        color={Colors.SECONDARY}
                                    />
                                    {t('managerAssessmentEvaluation.insights.retry')}
                                </button>
                            )}
                        </div>
                    ) : insight ? (
                        <div
                            className={'flex flex-col gap-4'}
                            data-testid={'insights-content'}
                        >
                            {sections.map((section) => (
                                <div
                                    key={section.key}
                                    className={'flex flex-col gap-1.5'}
                                >
                                    {section.label && (
                                        <h4
                                            className={
                                                'text-secondary-700 text-body-sm font-semibold'
                                            }
                                        >
                                            {section.label}
                                        </h4>
                                    )}

                                    {section.text?.trim() ? (
                                        <Fragment>{renderSectionBody(section.text)}</Fragment>
                                    ) : (
                                        <p className={'text-sm leading-relaxed text-gray-400'}>
                                            {t('managerAssessmentEvaluation.insights.noData')}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            )}

            {hasInsight ? (
                <button
                    type={'button'}
                    onClick={handleCopy}
                    className={
                        'border-primary flex w-full items-center justify-center gap-2 rounded-lg border bg-white px-4 py-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-white/70'
                    }
                    data-testid={'copy-summary-button'}
                >
                    <Icon
                        name={copied ? 'essential-checkmark' : 'design-copy'}
                        size={14}
                        color={copied ? Colors.SUCCESS : Colors.BASE}
                    />

                    {copied
                        ? t('managerAssessmentEvaluation.insights.copied')
                        : t('managerAssessmentEvaluation.insights.copySummary')}
                </button>
            ) : null}
        </div>
    );
};
